import { resolveOllieFindingTargetPath } from './ollieFindingCards'
import { formatOllieAsOfLabel } from './ollieNoticedRows'

/**
 * Phase 4 step 45 — Ollie on Success Hub (Progress): "Ollie's pick" on
 * today's job and problems Ollie saw sorted, as proof in Locked in. Pure.
 * Figures and wording come from Express (`olliePick`, `resolved_wins`).
 */

export const SUCCESS_HUB_OLLIE_PICK_CTA_ACTION = 'ollie_finding'
export const SUCCESS_HUB_OLLIE_WINS_CHECK_ID = 'resolved_wins'
/** At most this many "sorted" receipts, so wins never crowd achievements. */
export const SUCCESS_HUB_OLLIE_MAX_WIN_RECEIPTS = 2

export const SUCCESS_HUB_OLLIE_COPY = Object.freeze({
  pickLabel: "Ollie's pick",
})

/**
 * Express sends each sorted problem's mark from its check category (FS, CO…).
 * Items cached before that get a neutral mark — never OL, which is the
 * "Order in one ask" badge.
 */
export const SUCCESS_HUB_OLLIE_WIN_FALLBACK_BADGE = Object.freeze({
  initials: 'OK',
  categoryId: 'kitchen',
})
const SUCCESS_HUB_OLLIE_WIN_LINE = 'Sorted with Ollie.'

const winBadge = mark => {
  const initials = String(mark?.initials || '').trim()
  const categoryId = String(mark?.categoryId || '').trim()
  if (!initials || !categoryId)
    return { ...SUCCESS_HUB_OLLIE_WIN_FALLBACK_BADGE }
  return { initials, categoryId }
}

export const SUCCESS_HUB_OLLIE_PICK_CTA_MODES = Object.freeze({
  NAVIGATE: 'navigate',
  THREAD: 'thread',
})

/**
 * Today's job from an Ollie finding → label line + what its button does.
 *
 * @param {object|null} recommendation — `mapSuccessHubHeroToRecommendation`
 * @param {{ venueId?: string, now?: Date }} [options]
 */
export function buildSuccessHubOlliePick(
  recommendation,
  { venueId, now = new Date() } = {},
) {
  const pick = recommendation?.olliePick
  if (!pick?.checkId) return null
  const cta = recommendation.cta || {}
  const path =
    cta.kind === 'open'
      ? resolveOllieFindingTargetPath(venueId, cta.target, cta.params)
      : null
  const asOf = formatOllieAsOfLabel(pick.asOf, now)
  return {
    label: pick.label || SUCCESS_HUB_OLLIE_COPY.pickLabel,
    asOfLabel: asOf ? `as of ${asOf}` : null,
    checkId: pick.checkId,
    fingerprint: pick.fingerprint || null,
    finding: {
      checkId: pick.checkId,
      intentId: recommendation.intentId || `ollie:${pick.checkId}`,
      featureId: recommendation.featureId || null,
      fingerprint: pick.fingerprint || null,
    },
    cta: {
      mode: path
        ? SUCCESS_HUB_OLLIE_PICK_CTA_MODES.NAVIGATE
        : SUCCESS_HUB_OLLIE_PICK_CTA_MODES.THREAD,
      path,
      action: cta.actionId
        ? {
            id: cta.actionId,
            label: cta.label || null,
            kind: cta.kind || null,
            target: cta.target ?? null,
          }
        : null,
    },
  }
}

export const isSuccessHubOlliePickAction = action =>
  action === SUCCESS_HUB_OLLIE_PICK_CTA_ACTION

/**
 * Problems Ollie flagged that are now sorted → Locked-in receipts.
 *
 * @param {object|null} winsFinding — the served `resolved_wins` finding
 * @returns {Array<{ id: string, title: string, earnedAt: string|null,
 *   whyItMatters: string, badge: { initials: string, categoryId: string } }>}
 */
export function buildSuccessHubOllieWinReceipts(winsFinding) {
  if (winsFinding?.checkId !== SUCCESS_HUB_OLLIE_WINS_CHECK_ID) return []
  return (winsFinding.items || [])
    .filter(item => item?.id && String(item.label || '').trim())
    .slice(0, SUCCESS_HUB_OLLIE_MAX_WIN_RECEIPTS)
    .map(item => ({
      id: item.id,
      title: String(item.label).trim(),
      earnedAt: item.meta?.resolvedAt || null,
      whyItMatters: SUCCESS_HUB_OLLIE_WIN_LINE,
      badge: winBadge(item.meta?.mark),
    }))
}
